import { useCallback, useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type {
	AiSuggestionsEntryPoint,
	AiSuggestionsEmptyStateType,
	AiSuggestionsEventPayload,
	AiSuggestionsRightRailEntryPoint,
} from '@atlaskit/editor-common/analytics';
import { ACTION, ACTION_SUBJECT } from '@atlaskit/editor-common/analytics/types/enums';

import type { Suggestion } from '@atlassian/editor-plugin-ai-suggestions';

export const SUGGESTIONS_PANEL_ANALYTICS_SOURCE = 'suggestionsPanel';

export const SUGGESTIONS_PANEL_EMPTY_STATE_ENTRY_POINT: AiSuggestionsEntryPoint =
	'suggestionsPanelEmptyState';

type SuggestionAnalyticsSnapshot = Pick<
	Suggestion,
	'action' | 'actionKind' | 'createdAt' | 'toolCalls' | 'type'
>;

type RightRailViewedTrackData = Omit<
	Extract<AiSuggestionsEventPayload, { action: ACTION.RIGHT_RAIL_VIEWED }>,
	'eventType'
> & {
	source: typeof SUGGESTIONS_PANEL_ANALYTICS_SOURCE;
};

type EmptyStateExposedTrackData = Omit<
	Extract<AiSuggestionsEventPayload, { action: ACTION.EMPTY_STATE_EXPOSED }>,
	'eventType'
> & {
	source: typeof SUGGESTIONS_PANEL_ANALYTICS_SOURCE;
};

type CancelledTrackData = Omit<
	Extract<AiSuggestionsEventPayload, { action: ACTION.CANCELLED }>,
	'eventType'
> & {
	source: typeof SUGGESTIONS_PANEL_ANALYTICS_SOURCE;
};

const getSuggestionAgeAttribute = (
	suggestion: Pick<Suggestion, 'createdAt'>,
): { suggestionAgeMs?: number } => {
	const createdAt = Date.parse(suggestion.createdAt);
	return Number.isFinite(createdAt) ? { suggestionAgeMs: Math.max(0, Date.now() - createdAt) } : {};
};

const getSuggestionRemixAttributes = (
	suggestion: Pick<Suggestion, 'action' | 'actionKind'>,
): { remixSubtype?: string; remixType?: string } => {
	if (suggestion.actionKind !== 'SUGGESTED_REMIX') {
		return {};
	}
	return {
		...(suggestion.action?.launchOptions?.dynamicUiType
			? { remixType: suggestion.action.launchOptions.dynamicUiType }
			: {}),
		...(suggestion.action?.launchOptions?.dynamicUiSubtype
			? { remixSubtype: suggestion.action.launchOptions.dynamicUiSubtype }
			: {}),
	};
};

const getAffectedBlocks = (suggestion: SuggestionAnalyticsSnapshot): number => {
	const localIds = [
		...(suggestion.action?.localIds ?? []),
		...(suggestion.toolCalls?.flatMap((toolCall) =>
			toolCall.name === 'removeNodes'
				? (toolCall.arguments.localIds ?? [])
				: toolCall.arguments.localId
					? [toolCall.arguments.localId]
					: [],
		) ?? []),
	];

	return new Set(localIds).size;
};

export const useEmptyStateExposedAnalytics = ({
	emptyStateType,
	enabled,
}: {
	emptyStateType: AiSuggestionsEmptyStateType;
	enabled: boolean;
}): void => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const lastExposedEmptyStateRef = useRef<AiSuggestionsEmptyStateType | null>(null);

	useEffect(() => {
		if (!enabled) {
			lastExposedEmptyStateRef.current = null;
			return;
		}
		if (lastExposedEmptyStateRef.current === emptyStateType) {
			return;
		}

		const data = {
			source: SUGGESTIONS_PANEL_ANALYTICS_SOURCE,
			action: ACTION.EMPTY_STATE_EXPOSED,
			actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
			attributes: { emptyStateType },
		} satisfies EmptyStateExposedTrackData;

		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data,
		}).fire();
		lastExposedEmptyStateRef.current = emptyStateType;
	}, [createAnalyticsEvent, emptyStateType, enabled]);
};

export const useReasoningViewedAnalytics = (): ((suggestion: Suggestion) => void) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return useCallback(
		(suggestion: Suggestion) => {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: SUGGESTIONS_PANEL_ANALYTICS_SOURCE,
					action: ACTION.REASONING_VIEWED,
					actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
					attributes: {
						actionKind: suggestion.actionKind ?? 'SUGGESTED_EDIT',
						affectedBlocks: getAffectedBlocks(suggestion),
						agentId: suggestion.createdByAgent?.accountId,
						hasSources: Boolean(suggestion.sources?.length),
						interactionPoint: 'sidebar',
						reasoningCharacterCount: suggestion.reasoning?.length ?? 0,
						...getSuggestionAgeAttribute(suggestion),
						...getSuggestionRemixAttributes(suggestion),
						suggestionId: suggestion.id,
						suggestionType: suggestion.type,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);
};

export const useCancelledAnalytics = (): (() => void) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return useCallback(() => {
		const data = {
			source: SUGGESTIONS_PANEL_ANALYTICS_SOURCE,
			action: ACTION.CANCELLED,
			actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
			attributes: { interactionPoint: 'sidebar' },
		} satisfies CancelledTrackData;

		createAnalyticsEvent({ type: 'sendTrackEvent', data }).fire();
	}, [createAnalyticsEvent]);
};

export const useSuggestionsPanelOpenAnalytics = ({
	entryPoint,
	isFullPanelLoading,
	suggestions,
}: {
	entryPoint: AiSuggestionsRightRailEntryPoint;
	isFullPanelLoading: boolean;
	suggestions?: readonly SuggestionAnalyticsSnapshot[];
}): void => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const hasFiredRef = useRef(false);

	useEffect(() => {
		if (hasFiredRef.current || isFullPanelLoading) {
			return;
		}

		const suggestionDetails = (suggestions ?? []).map((suggestion) => ({
			suggestionType: suggestion.type,
			actionKind: suggestion.actionKind ?? 'SUGGESTED_EDIT',
			affectedBlocks: getAffectedBlocks(suggestion),
			...getSuggestionAgeAttribute(suggestion),
			...getSuggestionRemixAttributes(suggestion),
		}));
		const data = {
			source: SUGGESTIONS_PANEL_ANALYTICS_SOURCE,
			action: ACTION.RIGHT_RAIL_VIEWED,
			actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
			attributes: {
				entryPoint,
				numberOfSuggestions: suggestionDetails.length,
				suggestionDetails,
			},
		} satisfies RightRailViewedTrackData;

		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data,
		}).fire();
		hasFiredRef.current = true;
	}, [createAnalyticsEvent, entryPoint, isFullPanelLoading, suggestions]);
};
